import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Smartphone, Clock, KeyRound, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { theme, inputStyle, labelStyle } from '../../theme';
import { OralzyLogo } from '../../pages/LandingPage';

const card = {
    backgroundColor: theme.surface, borderRadius: '20px',
    padding: '36px 40px', width: '460px', maxWidth: '95vw',
    boxShadow: theme.shadowLg, border: `1px solid ${theme.border}`,
};
const btn = (color = theme.primary) => ({
    width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
    backgroundColor: color, color: 'white', fontWeight: '700', fontSize: '0.95rem',
    cursor: 'pointer', marginTop: '8px',
});

/**
 * Shown to a brand-new PC trying to use ORALZY. Three states:
 *  - 'request'         : form to submit access request to admin
 *  - 'awaiting-approval': admin hasn't approved yet — polling for status flip
 *  - 'awaiting-code'   : admin approved → enter the code received on mobile
 *  - 'rejected'        : admin denied
 */
export default function NewDeviceRequest({ API_BASE, fingerprint, deviceCtx, initialStatus = 'unknown', onApproved }) {
    const [phase, setPhase] = useState(initialStatus === 'awaiting-code' ? 'awaiting-code'
                              : initialStatus === 'pending' ? 'awaiting-approval'
                              : initialStatus === 'rejected' ? 'rejected'
                              : 'request');
    const [err, setErr] = useState('');
    const [busy, setBusy] = useState(false);

    // Form state
    const [deviceName, setDeviceName] = useState('');
    const [requesterName, setRequesterName] = useState('');
    const [requesterMobile, setRequesterMobile] = useState('+91');
    const [requesterChannel, setRequesterChannel] = useState('whatsapp');
    const [reason, setReason] = useState('');

    // Code entry
    const [code, setCode] = useState('');

    // Poll backend while awaiting approval
    const pollRef = useRef();
    useEffect(() => {
        if (phase !== 'awaiting-approval') {
            if (pollRef.current) clearInterval(pollRef.current);
            return;
        }
        pollRef.current = setInterval(async () => {
            try {
                const r = await axios.post(`${API_BASE}/security/device-check`, { fingerprint });
                if (r.data.status === 'awaiting-code') setPhase('awaiting-code');
                else if (r.data.status === 'approved') { onApproved && onApproved(); }
                else if (r.data.status === 'rejected') setPhase('rejected');
            } catch {}
        }, 5000);
        return () => clearInterval(pollRef.current);
    }, [phase, API_BASE, fingerprint, onApproved]);

    const submitRequest = async () => {
        if (!deviceName || !requesterName || !requesterMobile) return setErr('All fields required');
        setErr(''); setBusy(true);
        try {
            await axios.post(`${API_BASE}/security/device-request`, {
                fingerprint, deviceName, requesterName, requesterMobile, requesterChannel, reason,
                userAgent: deviceCtx.userAgent, platform: deviceCtx.platform,
            });
            setPhase('awaiting-approval');
        } catch (e) {
            setErr(e.response?.data?.error || 'Request failed');
        }
        setBusy(false);
    };

    const submitCode = async () => {
        if (!code || code.length !== 6) return setErr('Enter the 6-digit code');
        setErr(''); setBusy(true);
        try {
            await axios.post(`${API_BASE}/security/device-verify-code`, { fingerprint, code });
            onApproved && onApproved();
        } catch (e) {
            setErr(e.response?.data?.error || 'Invalid or expired code');
        }
        setBusy(false);
    };

    return (
        <div style={{minHeight:'100vh', backgroundColor:theme.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
            <div style={card}>
                <div style={{textAlign:'center', marginBottom:'20px'}}>
                    <OralzyLogo height={42}/>
                </div>

                {err && (
                    <div style={{padding:'10px 14px', borderRadius:'8px', backgroundColor:'#fef2f2', color:theme.danger, fontSize:'0.85rem', marginBottom:'14px', display:'flex', gap:'8px', alignItems:'center'}}>
                        <AlertTriangle size={16}/>{err}
                    </div>
                )}

                {phase === 'request' && (<>
                    <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px'}}>
                        <Smartphone size={20} color={theme.warning}/>
                        <h2 style={{margin:0, color:theme.navy, fontSize:'1.1rem'}}>New Device — Access Required</h2>
                    </div>
                    <p style={{fontSize:'0.86rem', color:theme.textMuted, margin:'0 0 10px'}}>
                        This PC is not registered. Submit a request and the admin will approve it.
                    </p>
                    <div style={{fontSize:'0.74rem', color:theme.textMuted, margin:'0 0 16px', padding:'8px 10px', backgroundColor:'#f8fafc', borderRadius:'6px', borderLeft:`3px solid ${theme.info}`}}>
                        💡 If this PC was approved before, hardware may have changed (GPU/monitor/RAM swap, OS reinstall). Re-approval is required by design.
                    </div>
                    <label style={labelStyle}>This PC's name *</label>
                    <input value={deviceName} onChange={e=>setDeviceName(e.target.value)} style={inputStyle} placeholder="e.g. Reception PC"/>
                    <label style={labelStyle}>Your name *</label>
                    <input value={requesterName} onChange={e=>setRequesterName(e.target.value)} style={inputStyle}/>
                    <label style={labelStyle}>Your mobile (with country code) *</label>
                    <input value={requesterMobile} onChange={e=>setRequesterMobile(e.target.value)} style={inputStyle} placeholder="+9198XXXXXXXX"/>
                    <label style={labelStyle}>Send unlock code via</label>
                    <div style={{display:'flex', gap:'8px', marginBottom:'12px'}}>
                        {[
                            { v:'whatsapp', l:'WhatsApp' },
                            { v:'sms', l:'SMS' },
                            { v:'both', l:'Both' },
                        ].map(opt => (
                            <button key={opt.v} onClick={()=>setRequesterChannel(opt.v)}
                                style={{flex:1, padding:'10px', borderRadius:'8px',
                                    border:`1px solid ${requesterChannel===opt.v ? theme.primary : theme.border}`,
                                    backgroundColor: requesterChannel===opt.v ? theme.primary+'18' : 'white',
                                    color: requesterChannel===opt.v ? theme.primary : theme.textMuted,
                                    fontWeight:'600', cursor:'pointer', fontSize:'0.85rem'}}>{opt.l}</button>
                        ))}
                    </div>
                    <label style={labelStyle}>Reason (optional)</label>
                    <textarea value={reason} onChange={e=>setReason(e.target.value)} style={{...inputStyle, resize:'vertical', minHeight:'60px'}} placeholder="e.g. Working from home today"/>
                    <button onClick={submitRequest} disabled={busy} style={btn()}>{busy ? 'Submitting…' : 'Request Access'}</button>
                </>)}

                {phase === 'awaiting-approval' && (
                    <div style={{textAlign:'center', padding:'20px 0'}}>
                        <Clock size={48} color={theme.warning} style={{marginBottom:'10px'}}/>
                        <h2 style={{margin:'0 0 8px', color:theme.navy}}>Waiting for admin approval</h2>
                        <p style={{color:theme.textMuted, fontSize:'0.88rem', margin:'0 0 16px'}}>
                            Admin has been notified. You'll receive a 6-digit unlock code on <strong>{requesterMobile || 'your mobile'}</strong> via {requesterChannel} as soon as they approve.
                        </p>
                        <div style={{padding:'10px', backgroundColor:'#f1f5f9', borderRadius:'8px', fontSize:'0.78rem', color:theme.textMuted}}>
                            This page checks every 5 seconds. Keep it open.
                        </div>
                    </div>
                )}

                {phase === 'awaiting-code' && (<>
                    <div style={{display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px'}}>
                        <KeyRound size={20} color={theme.success}/>
                        <h2 style={{margin:0, color:theme.navy, fontSize:'1.1rem'}}>Approved! Enter unlock code</h2>
                    </div>
                    <p style={{fontSize:'0.86rem', color:theme.textMuted, margin:'0 0 16px'}}>
                        Admin approved your request. Check {requesterChannel} on your mobile for the 6-digit code (valid 10 min).
                    </p>
                    <input value={code} onChange={e=>setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                        style={{...inputStyle, fontSize:'1.6rem', textAlign:'center', letterSpacing:'0.5em', fontFamily:'monospace'}}
                        placeholder="000000" maxLength={6}/>
                    <button onClick={submitCode} disabled={busy} style={btn(theme.success)}>{busy ? 'Verifying…' : 'Unlock This PC'}</button>
                </>)}

                {phase === 'rejected' && (
                    <div style={{textAlign:'center', padding:'20px 0'}}>
                        <AlertTriangle size={48} color={theme.danger} style={{marginBottom:'10px'}}/>
                        <h2 style={{margin:'0 0 8px', color:theme.danger}}>Access Denied</h2>
                        <p style={{color:theme.textMuted, fontSize:'0.88rem'}}>Admin rejected this device's access request. Contact the admin if you believe this is a mistake.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
